<template>
    <div ref="resultlist" class="flex" data-component="AmsResultlist">
        <ams-sidefilter class="mr-24 min-w-[255px] max-w-[255px] hidden screen-xxl:block" />
        <div class="relative flex flex-col flex-grow max-w-full min-w-0 mb-48 screen-m:mb-56 screen-l:mb-72">
            <cs-sort
                v-if="!hasStickyFilter"
                v-model="amsSearchStore.sort"
                class="self-end py-[14px]"
                :options="carSortOptions"
            />
            <ams-sticky-filter v-else class="-mt-8 screen-m:mt-0" :hasStickyFilterTags="hasStickyFilterTags" />
            <error-notification />
            <section v-if="!amsSearchStore.error" key="list" class="duration-300">
                <ul ref="listRef">
                    <no-items-notification />
                    <template v-for="(item, index) in amsSearchStore.results.items" :key="item.externalId">
                        <first-additional-item-notification :item="item" />
                        <ams-list-item
                            ref="itemComponentsRef"
                            :for-location-name="globalStore.requestContext?.location.name"
                            :hasPriceIndicator="hasPriceIndicator"
                            :item="item"
                            class="mb-16 screen-m:mb-24"
                            @update:visible="openPriceIndicatorModal()"
                            @click="onItemClick($event, item)"
                            @price-indicator-click="onPriceIndicatorClick(item)"
                        />

                        <li
                            v-if="
                                $slots[slotNameforPosition(index, 'desktop')] ||
                                $slots[slotNameforPosition(index, 'mobile')]
                            "
                            class="max-w-[calc(100vw-32px)]"
                        >
                            <slot :name="slotNameforPosition(index, 'desktop')" />
                            <slot :name="slotNameforPosition(index, 'mobile')" />
                        </li>

                        <slot :name="'disturber-' + index" />
                    </template>
                </ul>
                <cs-linear-pagination v-model:current="page" :last="totalPages" />
                <ams-resultlist-legal-text class="mt-32" />
                <ams-resultlist-json-ld />
            </section>
            <ams-resultlist-loading-spinner />
        </div>

        <ams-price-indicator-modal
            v-if="hasPriceIndicator && activeItem"
            v-model:visible="priceIndicatorModalVisible"
            :item="activeItem"
        />
    </div>
</template>

<script lang="ts" setup>
import AmsListItem from '../AmsListItem'
import AmsResultlistJsonLd from './AmsResultlistJsonLd.vue'
import NoItemsNotification from './notifications/NoItemsNotification.vue'
import FirstAdditionalItemNotification from './notifications/FirstAdditionalItemNotification.vue'
import ErrorNotification from './notifications/ErrorNotification.vue'
import AmsResultlistLoadingSpinner from './AmsResultlistLoadingSpinner.vue'
import AmsResultlistLegalText from './AmsResultlistLegalText.vue'
import AmsSidefilter from '../AmsSidefilter'
import AmsStickyFilter from '../AmsStickyFilter'
import { CsSort, CsLinearPagination } from '@sunrise/components'
import { useInitializedAmsSearchStore } from '../../store/amsSearch'
import { useGlobalStore } from '@sunrise/shared'
import { AmsItem, SortKeys } from '../../services/amsSearch/amsSearch.interface'
import { watch, ref, computed } from 'vue'
import { useItemsIntersectionObserver } from '../../composables/useItemsIntersectionObserver/useItemsIntersectionObserver'
import { Ads } from '@sunrise/types'
import AmsPriceIndicatorModal from '../AmsPriceIndicator/AmsPriceIndicatorModal'
import { useScrollToResultlist } from '../../composables/useScrollToResultlist/useScrollToResultlist'
import { slotNameforPosition } from '../../utils/cmsUtils'

defineProps({
    hasPriceIndicator: Boolean,
    hasStickyFilter: Boolean,
    hasStickyFilterTags: Boolean,
})

const amsSearchStore = useInitializedAmsSearchStore()
const { scrollToResultlist } = useScrollToResultlist()

const emit = defineEmits<{
    (e: 'itemClick', event: MouseEvent, item: AmsItem): void
    (e: 'itemInViewport', item: AmsItem): void
    (e: 'priceIndicatorClick', item: AmsItem, isOpened: boolean): void
}>()

const onItemClick = (event: MouseEvent, item: AmsItem) => {
    emit('itemClick', event, item)
}

const activeItem = ref<AmsItem>()

const onPriceIndicatorClick = (item: AmsItem) => {
    openPriceIndicatorModal()

    activeItem.value = item
}

const listRef = ref<HTMLElement>()
const itemComponentsRef = ref<InstanceType<typeof AmsListItem>[]>()

useItemsIntersectionObserver({
    listRef,
    itemsComponents: itemComponentsRef,
    onItemInViewport: (item) => {
        emit('itemInViewport', item)
    },
})

const priceIndicatorModalVisible = ref(false)

watch(priceIndicatorModalVisible, () => {
    if (activeItem.value) {
        emit('priceIndicatorClick', activeItem.value, priceIndicatorModalVisible.value)
    }
})

const openPriceIndicatorModal = () => (priceIndicatorModalVisible.value = true)

enum SortValues {
    OurRecommendationADesc = 'Unsere Empfehlung',
    DistanceAscAndCpcDesc = 'Entfernung',
    LatestOffersAndCpcDesc = 'Neueste',
    FocusOnPriceADesc = 'Preis & Empfehlung',
    PriceAsc = 'Preis',
}

const carSortOptions = [
    { value: SortKeys.OurRecommendationADesc, label: SortValues.OurRecommendationADesc },
    { value: SortKeys.DistanceAscAndCpcDesc, label: SortValues.DistanceAscAndCpcDesc },
    { value: SortKeys.LatestOffersAndCpcDesc, label: SortValues.LatestOffersAndCpcDesc },
    { value: SortKeys.FocusOnPriceADesc, label: SortValues.FocusOnPriceADesc },
    { value: SortKeys.PriceAsc, label: SortValues.PriceAsc },
]

const globalStore = useGlobalStore()

const page = computed({
    async set(newPage: number) {
        await scrollToResultlist()
        amsSearchStore.page = newPage
    },
    get() {
        return amsSearchStore.page
    },
})

const totalPages = computed(() => Math.ceil(amsSearchStore.results.totalHits / amsSearchStore.results.pageSize))

watch(
    () => amsSearchStore.results,
    () => {
        window.refreshAds?.(Ads.RefreshReason.USER_INTERACTION)
    },
)
</script>
